import { Page } from "layouts/finance/shared";
import React, { useMemo, useState, useEffect } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  Card,
  TextField,
  Button,
  Menu,
  MenuItem,
  useMediaQuery,
  useTheme,
  InputAdornment,
  Select,
  Checkbox,
  Alert,
  Pagination,
  Skeleton,
  Grid,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import CsvDownloader from "react-csv-downloader";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import authorsTableData from "layouts/tables/data/authorsTableData";
import DynamicTable from "./table.js";

import EmptyData from "../../helpers/EmptyData";
import ErrorState from "../../helpers/ErrorState";

// ✅ Use context like merchant page
import { useDashboard } from "context/DashboardContext.js";

// If you already have this hook (like merchant page), we'll reuse it.
// If the path is different in your project, update it accordingly.
import { useTransactionFilters } from "../../hooks/useTransactionFilters.js";

import "./date-picker.css";
import "./action-menu.js";

const Tables = () => {
  const { columns } = authorsTableData;

  // ✅ FROM CONTEXT (same pattern as merchant page)
  const { transactions, loadingTransactions, transactionsError, refreshData } =
    useDashboard();

  // UI State
  const [anchorEl, setAnchorEl] = useState(null);

  // Filtering state
  const [searchText, setSearchText] = useState("");
  const [checked, setChecked] = useState(false); // status checkbox
  const [dateChecked, setDateChecked] = useState(false); // date checkbox
  const [selectedStatus, setSelectedStatus] = useState("");
  const [selectedValue, setSelectedValue] = useState("0"); // for dropdown UI

  // Date range (keep your defaults)
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setMonth(date.getMonth() - 3);
    date.setHours(0, 0, 0, 0);
    return date;
  });

  const [endDate, setEndDate] = useState(() => new Date());

  // Pagination state (same as merchant page)
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Responsive button size (kept from your admin page)
  const theme = useTheme();
  const isXSmallScreen = useMediaQuery(theme.breakpoints.down("xs"));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMediumScreen = useMediaQuery(theme.breakpoints.down("md"));
  const buttonSize = isXSmallScreen
    ? "small"
    : isSmallScreen
    ? "small"
    : isMediumScreen
    ? "small"
    : "large";

  // Menu handlers
  const handleOpenMenu = (event) => setAnchorEl(event.currentTarget);
  const handleCloseMenu = () => setAnchorEl(null);

  // Search
  const handleSearch = (event) => {
    setSearchText(event.target.value);
    setPage(1);
  };

  // Status checkbox
  const handleCheckboxChange = (event) => {
    const val = event.target.checked;
    setChecked(val);
    setPage(1);

    // if turning off status filter, reset status
    if (!val) {
      setSelectedStatus("");
      setSelectedValue("0");
    }
  };

  // Date checkbox
  const handleDateCheckboxChange = (event) => {
    setDateChecked(event.target.checked);
    setPage(1);
  };

  // Status dropdown
  const handleValueChange = (event) => {
    const value = event.target.value;
    setSelectedValue(value);
    setPage(1);

    let statusString = "";
    switch (value) {
      case "1":
        statusString = "SUCCESS";
        break;
      case "2":
        statusString = "PENDING";
        break;
      case "3":
        statusString = "AUTHORIZED";
        break;
      case "4":
        statusString = "FAILED";
        break;
      case "6":
        statusString = "PENDING_APPROVAL";
        break;
      case "7":
        statusString = "APPROVED";
        break;
      case "8":
        statusString = "REJECTED";
        break;
      case "5":
        statusString = "REFUNDED";
        break;
      default:
        statusString = "";
    }
    setSelectedStatus(statusString);
  };

  // ✅ USE SAME FILTER HOOK AS MERCHANT PAGE
  const filteredTransactions = useTransactionFilters({
    transactions: Array.isArray(transactions) ? transactions : [],
    searchText,
    checked,
    selectedStatus,
    dateChecked,
    startDate,
    endDate,
  });

  // Reset to page 1 if filters reduce data such that current page is invalid
  useEffect(() => {
    setPage(1);
  }, [searchText, checked, selectedStatus, dateChecked, startDate, endDate]);

  // ✅ PAGINATION CALCULATION (same pattern)
  const totalPages = Math.ceil(filteredTransactions.length / pageSize) || 1;

  const paginatedData = useMemo(() => {
    return filteredTransactions.slice((page - 1) * pageSize, page * pageSize);
  }, [filteredTransactions, page, pageSize]);

  const handlePageChange = (_event, value) => setPage(value);

  const handlePageSizeChange = (event) => {
    setPageSize(event.target.value);
    setPage(1);
  };

  return (
    <Page title="Transactions" description="Search payment activity, inspect transaction details, and export records.">


      {/* ERROR BANNER (same as merchant) */}
      {transactionsError && (
        <Alert variant='outlined' severity='error'>
          Failed to fetch data, check network connection!
        </Alert>
      )}

      <SoftBox py={3}>
        <SoftBox mb={3}>
          <Card>
            {/* HEADER */}
            <SoftBox
              display='flex'
              justifyContent='space-between'
              alignItems='center'
              p={3}
            >
              <SoftTypography variant='h6'>
                Merchant Transactions
              </SoftTypography>

              {/* SEARCH + CSV EXPORT GROUP */}
              <SoftBox display='flex' alignItems='center' gap={2}>
                <TextField
                  placeholder='Transaction Reference'
                  variant='outlined'
                  value={searchText}
                  onChange={handleSearch}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position='end'>
                        <SearchIcon
                          style={{ cursor: "pointer", color: "blue" }}
                        />
                      </InputAdornment>
                    ),
                  }}
                />

                {/* EXPORT CURRENT PAGE */}
                <CsvDownloader
                  datas={paginatedData}
                  filename={`transactions_page_${page}`}
                  extension='.csv'
                >
                  <Button
                    variant='outlined'
                    size='small'
                    sx={{ height: 30, borderRadius: "8px", color: "darkblue" }}
                  >
                    Export Page
                  </Button>
                </CsvDownloader>

                {/* EXPORT ALL FILTERED */}
                {/* <CsvDownloader
                  datas={filteredTransactions}
                  filename={`transactions_all_filtered`}
                  extension='.csv'
                >
                  <Button
                    variant='outlined'
                    size='small'
                    sx={{ height: 30, borderRadius: "8px", color: "darkblue" }}
                  >
                    Export All
                  </Button>
                </CsvDownloader> */}
              </SoftBox>
            </SoftBox>

            {/* FILTER BUTTON */}
            <div
              style={{
                width: "15rem",
                marginLeft: "1rem",
                marginBottom: "2rem",
              }}
            >
              <Button
                onClick={handleOpenMenu}
                variant='outlined'
                size={buttonSize}
                sx={{ height: 30, borderRadius: "8px", color: "darkblue" }}
              >
                Filter
              </Button>
            </div>

            {/* FILTER MENU */}
            <Menu
              id='filter-menu'
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleCloseMenu}
              PaperProps={{
                style: {
                  minHeight: "450px",
                  width: "500px",
                  padding: "10px",
                },
              }}
            >
              {/* STATUS FILTER */}
              <MenuItem>
                <Checkbox checked={checked} onChange={handleCheckboxChange} />
                Filter by Status
              </MenuItem>

              <MenuItem>
                <Select
                  value={selectedValue}
                  onChange={handleValueChange}
                  disabled={!checked}
                  displayEmpty
                  style={{ width: "100%" }}
                  IconComponent={() => <ArrowDropDownIcon />}
                >
                  <MenuItem value='0'>Select Status</MenuItem>
                  <MenuItem value='1'>Successful</MenuItem>
                  <MenuItem value='2'>Pending</MenuItem>
                  <MenuItem value='3'>Authorized</MenuItem>
                  <MenuItem value='4'>Failed</MenuItem>
                  <MenuItem value='5'>Refunded</MenuItem>
                  <MenuItem value='6'>Pending approval</MenuItem>
                  <MenuItem value='7'>Approved</MenuItem>
                  <MenuItem value='8'>Rejected</MenuItem>
                </Select>
              </MenuItem>

              {/* DATE FILTER */}
              <MenuItem>
                <Checkbox
                  checked={dateChecked}
                  onChange={handleDateCheckboxChange}
                />
                Filter by Date
              </MenuItem>

              <MenuItem>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-around",
                    width: "100%",
                  }}
                >
                  <div style={{ marginRight: "4px" }}>
                    <h5>Start</h5>
                    <DatePicker
                      selected={startDate}
                      dateFormat='yyyy/MM/dd'
                      onChange={(date) => setStartDate(date)}
                      className='custom-date-picker'
                      disabled={!dateChecked}
                      maxDate={
                        endDate ? new Date(endDate.getTime() - 86400000) : null
                      }
                    />
                  </div>

                  <div style={{ marginLeft: "4px" }}>
                    <h5>End</h5>
                    <DatePicker
                      selected={endDate}
                      dateFormat='yyyy/MM/dd'
                      onChange={(date) => setEndDate(date)}
                      className='custom-date-picker'
                      disabled={!dateChecked}
                      minDate={
                        startDate
                          ? new Date(startDate.getTime() + 86400000)
                          : null
                      }
                    />
                  </div>
                </div>
              </MenuItem>
            </Menu>

            {/* TABLE AREA */}
            <SoftBox>
              {/* Table - do not change DynamicTable internals. Just pass paginated */}
              {!loadingTransactions && !transactionsError && (
                <DynamicTable
                  columns={columns}
                  filteredTransactions={paginatedData}
                  exportToCsv={paginatedData}
                />
              )}

              {/* Skeleton loading state */}
              {loadingTransactions && (
                <SoftBox my={4} mx={4}>
                  <Grid container spacing={2}>
                    <SoftTypography
                      variant='button'
                      color='text'
                      fontWeight='medium'
                    >
                      Fetching your transactions Data...
                    </SoftTypography>
                    <Grid item xs={12} lg={12}>
                      {[...Array(6)].map((_, idx) => (
                        <Skeleton
                          key={idx}
                          variant='rectangular'
                          width='100%'
                          height={20}
                          sx={{ marginBottom: "1rem", borderRadius: "12px" }}
                        />
                      ))}
                    </Grid>
                  </Grid>
                </SoftBox>
              )}

              {/* Empty & Error states */}
              {!loadingTransactions &&
                (!transactionsError &&
                Array.isArray(transactions) &&
                transactions.length === 0 ? (
                  <EmptyData message='No Transaction data available.' />
                ) : transactionsError ? (
                  <ErrorState
                    message='Failed to fetch data!'
                    onRetry={refreshData}
                    loading={loadingTransactions}
                  />
                ) : null)}
            </SoftBox>

            {/* PAGINATION */}
            {!loadingTransactions &&
              !transactionsError &&
              filteredTransactions.length > 0 && (
                <SoftBox
                  display='flex'
                  justifyContent='space-between'
                  alignItems='center'
                  width='100%'
                  py={3}
                  px={2}
                >
                  <Pagination
                    count={totalPages}
                    page={page}
                    onChange={handlePageChange}
                    shape='rounded'
                    color='primary'
                  />

                  <SoftBox display='flex' alignItems='center' gap={1}>
                    <SoftTypography variant='caption'>
                      Items/page:
                    </SoftTypography>
                    <Select
                      value={pageSize}
                      onChange={handlePageSizeChange}
                      size='small'
                      style={{ height: 20 }}
                    >
                      <MenuItem value={10}>10</MenuItem>
                      <MenuItem value={20}>20</MenuItem>
                      <MenuItem value={50}>50</MenuItem>
                      <MenuItem value={70}>70</MenuItem>
                      <MenuItem value={100}>100</MenuItem>
                    </Select>
                  </SoftBox>
                </SoftBox>
              )}
          </Card>
        </SoftBox>
      </SoftBox>


    </Page>
  );
};

export default Tables;
